import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Report, SupportInquiry, PaginatedResponse } from '../../types/app';

interface ReportsState {
  reports: PaginatedResponse<Report> | null;
  inquiries: PaginatedResponse<SupportInquiry> | null;
  selectedReport: Report | null;
  selectedInquiry: SupportInquiry | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
}

const initialState: ReportsState = {
  reports: null,
  inquiries: null,
  selectedReport: null,
  selectedInquiry: null,
  isLoading: false,
  isUpdating: false,
  error: null,
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    fetchReportsStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchReportsSuccess: (state, action: PayloadAction<PaginatedResponse<Report>>) => {
      state.isLoading = false;
      state.reports = action.payload;
      state.error = null;
    },
    fetchReportsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    fetchInquiriesStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchInquiriesSuccess: (state, action: PayloadAction<PaginatedResponse<SupportInquiry>>) => {
      state.isLoading = false;
      state.inquiries = action.payload;
      state.error = null;
    },
    fetchInquiriesFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    selectReport: (state, action: PayloadAction<Report>) => {
      state.selectedReport = action.payload;
    },
    selectInquiry: (state, action: PayloadAction<SupportInquiry>) => {
      state.selectedInquiry = action.payload;
    },
    updateReportStart: (state) => {
      state.isUpdating = true;
      state.error = null;
    },
    updateReportSuccess: (state, action: PayloadAction<Report>) => {
      state.isUpdating = false;
      state.selectedReport = action.payload;
      
      // Update report in the list
      if (state.reports) {
        const index = state.reports.items.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.reports.items[index] = action.payload;
        }
      }
      state.error = null;
    },
    updateReportFailure: (state, action: PayloadAction<string>) => {
      state.isUpdating = false;
      state.error = action.payload;
    },
    updateInquirySuccess: (state, action: PayloadAction<SupportInquiry>) => {
      state.isUpdating = false;
      state.selectedInquiry = action.payload;
      
      // Update inquiry in the list
      if (state.inquiries) {
        const index = state.inquiries.items.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.inquiries.items[index] = action.payload;
        }
      }
      state.error = null;
    },
    clearSelectedReport: (state) => {
      state.selectedReport = null;
    },
    clearSelectedInquiry: (state) => {
      state.selectedInquiry = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  fetchReportsStart,
  fetchReportsSuccess,
  fetchReportsFailure,
  fetchInquiriesStart,
  fetchInquiriesSuccess,
  fetchInquiriesFailure,
  selectReport,
  selectInquiry,
  updateReportStart,
  updateReportSuccess,
  updateReportFailure,
  updateInquirySuccess,
  clearSelectedReport,
  clearSelectedInquiry,
  clearError,
} = reportsSlice.actions;

export default reportsSlice.reducer; 